import { alertClasses } from '@mui/material/Alert';
import { backdropClasses } from '@mui/material/Backdrop';
import { buttonClasses } from '@mui/material/Button';
import { filledInputClasses } from '@mui/material/FilledInput';
import { formLabelClasses } from '@mui/material/FormLabel';
import { menuItemClasses } from '@mui/material/MenuItem';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { paginationItemClasses } from '@mui/material/PaginationItem';
import { alpha, Components, PaletteColor } from '@mui/material/styles';

import { darkThemePalette } from './palette';
import {
  CheckboxIconChecked,
  CheckboxIconIndeterminate,
  CheckboxIconUnchecked,
} from '../base/checkboxIcons';

import '@mui/lab/themeAugmentation';

/**
 * Creates and returns component overrides for a Material-UI theme.
 *
 * @param options - The options for creating theme components: darkThemePalette and typographyOptions
 * @returns The object that contains the component overrides for Material-UI theme components
 *
 * @public
 */
export const darkThemeComponents: Components = {
  MuiAlert: {
    styleOverrides: {
      action: {
        'a, button': {
          color: 'inherit',
        },
        color: 'inherit',
      },
      filledError: {
        [`.${alertClasses.icon}`]: {
          color: darkThemePalette.error.main,
        },
        backgroundColor: darkThemePalette.alert.error.background,
        color: darkThemePalette.alert.error.content,
      },
      filledInfo: {
        [`.${alertClasses.icon}`]: {
          color: darkThemePalette.info.main,
        },
        backgroundColor: darkThemePalette.alert.info.background,
        color: darkThemePalette.alert.info.content,
      },
      filledSuccess: {
        [`.${alertClasses.icon}`]: {
          color: darkThemePalette.success.contrastText,
        },
        backgroundColor: darkThemePalette.alert.success.background,
        color: darkThemePalette.alert.success.content,
      },
      filledWarning: {
        [`.${alertClasses.icon}`]: {
          color: darkThemePalette.warning.main,
        },
        backgroundColor: darkThemePalette.alert.warning.background,
        color: darkThemePalette.alert.warning.content,
      },
      message: {
        '*': {
          color: 'inherit',
        },
        color: 'inherit',
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        backgroundColor: darkThemePalette.neutral[300],
        color: '#000',
      },
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        [`&:not(.${backdropClasses.invisible})`]: {
          backgroundColor: alpha('#000', 0.5),
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      contained: {
        color: darkThemePalette.secondary.darkest,
      },
      containedSecondary: {
        backgroundColor: darkThemePalette.secondary.dark,
        color: darkThemePalette.secondary.contrastText,
      },
      outlinedSecondary: {
        color: darkThemePalette.text.secondary,
      },
      root: {
        [`&.${buttonClasses.text}.${buttonClasses.colorSecondary}`]: {
          color: darkThemePalette.text.secondary,
        },
      },
      textSecondary: {
        '&:hover': {
          backgroundColor: darkThemePalette.secondary.hovered,
        },
      },
    },
    variants: [
      {
        props: {
          color: 'inherit',
          variant: 'contained',
        },
        style: {
          backgroundColor: darkThemePalette.grey?.[300],
        },
      },
    ],
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: darkThemePalette.background.paper,
        boxShadow: '0px 0px 0px 0.5px rgba(0, 0, 0, 0.03), 0px 5px 22px 0px rgba(0, 0, 0, 0.04)',
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      checkedIcon: <CheckboxIconChecked />,
      color: 'primary',
      icon: <CheckboxIconUnchecked />,
      indeterminateIcon: <CheckboxIconIndeterminate />,
    },
  },
  MuiChip: {
    styleOverrides: {
      colorSuccess: (props) => ({
        backgroundColor:
          props.ownerState.variant === 'filled' ? darkThemePalette.success.main : undefined,
        borderColor:
          props.ownerState.variant === 'outlined' ? darkThemePalette.success.main : undefined,
      }),
      colorWarning: (props) => ({
        backgroundColor:
          props.ownerState.variant === 'filled' ? darkThemePalette.warning.main : undefined,
        borderColor:
          props.ownerState.variant === 'outlined'
            ? alpha(darkThemePalette.warning.main, 0.5)
            : undefined,
      }),
      filled: {
        color: darkThemePalette.secondary.darkest,
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      filledDefault: {
        color: darkThemePalette.text.primary,
      },
      icon: {
        color: darkThemePalette.action.active,
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        '& .MuiDataGrid-cell': {
          backgroundColor: darkThemePalette.background.table,
        },
        '& .MuiDataGrid-withBorderColor': {
          borderColor: darkThemePalette.divider,
        },
        ['--DataGrid-rowBorderColor']: darkThemePalette.divider,
      },
      row: {
        '&.Mui-selected': {
          '&:hover': {
            backgroundColor: darkThemePalette.primary.hovered,
          },
          backgroundColor: 'unset',
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: darkThemePalette.background.paper,
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: darkThemePalette.action.hover,
        },
        backgroundColor: 'transparent',
        borderColor: darkThemePalette.neutral[300],
        color: darkThemePalette.text.primary,
        [`&.${filledInputClasses.disabled}`]: {
          backgroundColor: 'transparent',
        },
        [`&.${filledInputClasses.focused}`]: {
          backgroundColor: 'transparent',
          borderColor: darkThemePalette.primary.main,
          boxShadow: `${darkThemePalette.primary.main} 0 0 0 2px`,
        },
        [`&.${filledInputClasses.error}`]: {
          borderColor: darkThemePalette.error.main,
          boxShadow: `${darkThemePalette.error.main} 0 0 0 2px`,
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        '&::placeholder': {
          color: darkThemePalette.text.secondary,
        },
      },
      root: {
        [`&.${filledInputClasses.root}`]: {
          [`&.${filledInputClasses.disabled}`]: {
            borderColor: darkThemePalette.divider,
          },
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        [`&.${formLabelClasses.filled}`]: {
          color: darkThemePalette.text.secondary,
        },
        [`&.${formLabelClasses.focused}`]: {
          color: darkThemePalette.primary.main,
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        color: darkThemePalette.text.primary,
        svg: {
          color: darkThemePalette.primary.light,
        },
        [`&.${menuItemClasses.selected}.bold`]: {
          backgroundColor: alpha(darkThemePalette.primary.main, 0.12),
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        borderColor: darkThemePalette.divider,
      },
      root: {
        '&:hover': {
          backgroundColor: darkThemePalette.action.hover,
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: darkThemePalette.divider,
          },
        },
        [`&.${outlinedInputClasses.focused}`]: {
          backgroundColor: 'transparent',
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: (darkThemePalette.primary as PaletteColor).main,
          },
        },
        [`&.${filledInputClasses.error}`]: {
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: (darkThemePalette.error as PaletteColor).main,
          },
        },
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        [`&.${paginationItemClasses.selected}`]: {
          '&:hover': {
            backgroundColor: darkThemePalette.primary.main,
            color: darkThemePalette.primary.contrastText,
          },
          backgroundColor: darkThemePalette.primary.main,
          color: darkThemePalette.primary.contrastText,
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: darkThemePalette.neutral[800],
        backgroundImage: 'none',
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      color: 'primary',
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: {
        '&.Mui-active,&.Mui-completed': {
          color: darkThemePalette.primary.main,
        },
        color: darkThemePalette.secondary.main,
      },
    },
  },
  MuiSwitch: {
    defaultProps: {
      color: 'primary',
    },
    styleOverrides: {
      switchBase: {
        color: darkThemePalette.neutral[500],
      },
      track: {
        backgroundColor: darkThemePalette.neutral[400],
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        backgroundColor: darkThemePalette.background.table,
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
        borderBottomColor: darkThemePalette.divider,
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: {
        backgroundColor: darkThemePalette.background.table,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:has(+.hidden)': {
          borderBottom: 'none !important',
        },
        '&:not(:last-child)': {
          borderBottomColor: darkThemePalette.divider,
        },
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        '& .MuiRadio-root:hover': {
          background: 'none',
        },
        '&.Mui-selected': {
          borderColor: darkThemePalette.primary.main,
        },
        '&:hover': {
          backgroundColor: darkThemePalette.primary.hovered,
          borderColor: darkThemePalette.primary.dark,
        },
        borderColor: darkThemePalette.action.active,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backdropFilter: 'blur(6px)',
        background: alpha(darkThemePalette.neutral[900], 0.8),
      },
    },
  },
};
